import React, { useCallback } from 'react';
import { OptionProps } from 'react-select';
import cn from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';

import { Tooltip } from '@components/common';

import { Icons } from '@icons/icons';

import { formatDate } from '@utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ICrowdCellsConfigPreset } from '@interfaces/crowdcell/crowdCells';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';

import styles from './PresetOptionComponent.module.scss';

export interface IPresetOptionProps<T> extends OptionProps<T> {
	handleEdit: (option: TSelectOption | null) => void;
	handleDelete: (option: TSelectOption | null) => void;
}

function PresetsOptionComponent(props: IPresetOptionProps<ICrowdCellsConfigPreset>): JSX.Element {
	const {
		selectOption,
		handleEdit,
		handleDelete,
		selectProps,
		data: { comment, name, createdAt, createdBy },
	} = props;

	const handleSelect = useCallback(() => {
		selectOption(props.data);
	}, [props.data, selectOption]);

	const onEdit = useCallback((e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();

		handleEdit(props.data);
		selectProps.onMenuClose();
	}, [handleEdit, props.data, selectProps]);

	const onDelete = useCallback((e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();

		handleDelete(props.data);
		selectProps.onMenuClose();
	}, [handleDelete, props.data, selectProps]);

	return (
		<div
			className={cn(styles.presetsOption, {
				[styles.active]: props.isSelected,
			})}
			onClick={handleSelect}
		>
			<div>
				<p
					className={styles.label}
				>
					{name}
				</p>
				<Tooltip
					content={comment}
					isDisabled={!comment?.length || comment?.trim().length <= 77}
					placement='top'
				>
					<p className={styles.comments}>
						{comment}
					</p>
				</Tooltip>
				<p
					className={styles.meta}
				>
					{
						`${createdBy?.firstName}
						${createdBy?.lastName}
						${formatDate(utcToZonedTime(createdAt!, timezoneUTC), formatTemplateWithTime)}`
					}
				</p>
			</div>
			<div className={styles.icons}>
				<Tooltip content='Edit'>
					<div
						className={styles.edit}
						onClick={onEdit}
					>
						{Icons.editIcon()}
					</div>
				</Tooltip>
				<Tooltip content='Delete'>
					<div
						className={styles.delete}
						onClick={onDelete}
					>
						{Icons.delete()}
					</div>
				</Tooltip>
			</div>
		</div>
	);
}

export default PresetsOptionComponent;
